import React from "react";
import {
    Box,
    Typography,
    Chip,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Edit as EditIcon,
    DeleteOutline as DeleteOutlineIcon,
    ArrowRightAlt as ArrowRightAltIcon,
    ChecklistRtl as ChecklistIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { LEVEL_BADGE, normalizeLevel } from "./impactConstants";
import { placementsOf } from "./segments";

const headerCellSx = { color: "#00438E", fontWeight: "bold" };

// Level holds a badge, so it is centred, header and cell together; the prose
// columns stay left, because text is read from its left edge.
const centeredHeaderCellSx = { ...headerCellSx, textAlign: "center" };

// Proportioned widths driven by a <colgroup> under `table-layout: fixed`:
// Segments · What is changing · As-is → Future state · Level · Additional info ·
// row actions. They sum to exactly 100% with no `minWidth`, so the table is
// exactly its container's width and never scrolls sideways.
//
// Prose absorbs the slack: As-is → Future state is the two-part cell that sets
// the row height, so it gets the most, then What is changing (a bold title over
// a description). Level is held to its badge, and the row actions to 8%+ so
// their four icons sit 2x2 rather than one per line.
const WIDTHS = ["15%", "22%", "29%", "6%", "18%", "10%"];

const chipSx = {
    maxWidth: "100%",
    height: "auto",
    "& .MuiChip-label": {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        py: 0.25,
        lineHeight: 1.25,
    },
};

// Denser rows: the register is a list to SCAN, and the default cell padding put
// about four rows on a screen. Nothing is clamped — the multi-line columns still
// show every line they have.
const denseTableSx = {
    tableLayout: "fixed",
    width: "100%",
    "& .MuiTableCell-root": { px: 1.25, py: 1, lineHeight: 1.35 },
    "& .MuiTableCell-head": { py: 1.25 },
};

// Prose wraps freely. The one guard: a long unbroken string (a pasted URL) would
// otherwise widen its cell even under fixed layout and reintroduce the scroll.
const proseSx = { overflowWrap: "anywhere" };

// Segment titles and choices are NAMES, not prose: `break-word` wraps between
// words and splits a word only when it cannot fit a line of its own.
const nameSx = { overflowWrap: "break-word", wordBreak: "normal", hyphens: "none" };

// Small coloured badge for an impact's Level.
export function LevelBadge({ value }) {
    const key = normalizeLevel(value);
    const meta = LEVEL_BADGE[key];
    if (!meta) {
        return <Chip size="small" variant="outlined" label={<FormattedMessage id="impact.level_na" defaultMessage="—" />} />;
    }
    return (
        <Chip
            size="small"
            label={<FormattedMessage id={`impact.level_short_${key}`} defaultMessage={meta.short} />}
            sx={{ ...chipSx, backgroundColor: meta.bg, color: meta.color, fontWeight: 600, minWidth: 52 }}
        />
    );
}

// The impacts table — every impact the segment filters let through, in one
// continuous list.
export default function ImpactsTable({
    impacts,
    // Either omitted (a project viewer) drops its row button.
    onEdit,
    onDelete,
    onActivities,
    // The project's segments, in order. The first column lists where each row
    // sits in them.
    segments = [],
}) {
    const intl = useIntl();
    // What a row placed in no segment says, rather than a blank cell that
    // reads as missing data: it applies to any choice of every segment.
    const notSet = intl.formatMessage({ id: "impact.segment_any", defaultMessage: "Any" });

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
        >
            {/* No `minWidth`: with fixed layout and widths summing to 100% the
                table is exactly the panel's width, so there is nothing to scroll
                sideways. Narrower than desktop it degrades by wrapping chips and
                clamping prose harder rather than by growing a scrollbar. */}
            <Table sx={denseTableSx}>
                <colgroup>
                    {WIDTHS.map((w, i) => (
                        <col key={i} style={{ width: w }} />
                    ))}
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>
                            <FormattedMessage id="impact.col_segments" defaultMessage="Segments" />
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                            <FormattedMessage id="impact.col_what_is_changing" defaultMessage="What is changing" />
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                            <FormattedMessage id="impact.col_as_is_to_be" defaultMessage="As-is → Future state" />
                        </TableCell>
                        <TableCell align="center" sx={centeredHeaderCellSx}>
                            <FormattedMessage id="impact.col_level" defaultMessage="Level" />
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                            <FormattedMessage id="impact.col_goal" defaultMessage="Additional info" />
                        </TableCell>
                        <TableCell align="right" sx={headerCellSx} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {impacts.map((impact) => {
                        const placements = placementsOf(impact, segments);
                        return (
                        <TableRow key={impact.id} hover sx={{ verticalAlign: "top" }}>
                            <TableCell
                                title={
                                    placements
                                        .map(({ segment, choice }) => `${segment.title}: ${choice.label}`)
                                        .join(" · ") || notSet
                                }
                                sx={nameSx}
                            >
                                {/* One line per segment the row is placed in: the
                                    choice, with its segment's title above it,
                                    quieter, because the title qualifies the
                                    choice rather than competing with it. */}
                                {placements.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                                        {notSet}
                                    </Typography>
                                ) : (
                                    placements.map(({ segment, choice }, i) => (
                                        <Box key={segment.id} sx={{ mt: i === 0 ? 0 : 0.5 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "text.secondary", display: "block", lineHeight: 1.2 }}
                                            >
                                                {segment.title}
                                            </Typography>
                                            <Typography variant="body2">{choice.label}</Typography>
                                        </Box>
                                    ))
                                )}
                            </TableCell>
                            <TableCell sx={proseSx}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {impact.what_is_changing}
                                </Typography>
                                {impact.impact_description && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                                    >
                                        {impact.impact_description}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell sx={proseSx}>
                                {impact.as_is || impact.to_be ? (
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="flex-start"
                                        useFlexGap
                                        flexWrap="wrap"
                                    >
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            {impact.as_is || "—"}
                                        </Typography>
                                        <ArrowRightAltIcon fontSize="small" sx={{ color: "text.disabled" }} />
                                        <Typography variant="body2">{impact.to_be || "—"}</Typography>
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                                        —
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                <LevelBadge value={impact.level} />
                            </TableCell>
                            {/* "Additional info" — the BE's `goal`. */}
                            <TableCell sx={proseSx}>
                                {impact.goal ? (
                                    <Typography variant="body2">{impact.goal}</Typography>
                                ) : (
                                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                                        —
                                    </Typography>
                                )}
                            </TableCell>
                            {/* No `nowrap` here. Three icon buttons need ~100px, and
                                forcing that inside a 12% cell is exactly what
                                pushed the table past its container and produced the
                                horizontal scrollbar. Let them wrap 2x2 when the
                                panel is narrow instead. */}
                            <TableCell align="right">
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                        rowGap: 0.25,
                                    }}
                                >
                                    {/* The execution view for this row — what is
                                        planned against it and how far it has got. */}
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: "activity.section",
                                            defaultMessage: "Activities",
                                        })}
                                        describeChild
                                    >
                                        <IconButton
                                            size="small"
                                            aria-label={intl.formatMessage(
                                                {
                                                    id: "activity.for_impact",
                                                    defaultMessage: "Activities for {what}",
                                                },
                                                { what: impact.what_is_changing }
                                            )}
                                            onClick={() => onActivities(impact)}
                                            sx={{ color: "#00438E" }}
                                        >
                                            <ChecklistIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {/* Omitted for a project viewer: read-only
                                        rows keep Activities and nothing else. */}
                                    {onEdit && (
                                    <Tooltip title={intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" })}>
                                        <IconButton size="small" onClick={() => onEdit(impact)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    )}
                                    {onDelete && (
                                    <Tooltip title={intl.formatMessage({ id: "general.delete", defaultMessage: "Delete" })}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete(impact)}
                                            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
